import { useState } from 'react';
import { useApp, ROLES } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Bell,
  Send,
  Info,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Search,
  Check,
  Trash2,
} from 'lucide-react';
import { format } from 'date-fns';

const ROLE_LABELS_MAP = {
  admin: 'Admin',
  supreme_manager: 'Supreme Manager',
  manager: 'Manager',
  quality_checker: 'Quality Checker',
  field_executive: 'Field Executive',
  vendor: 'Vendor',
  client: 'Client',
  business_partner: 'Business Partner',
  universal_qc: 'Universal QC',
  data_entry_operator: 'Data Entry Operator',
};

const notificationIcons = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: XCircle,
};

const notificationColors = {
  info: 'text-blue-700 bg-blue-50 border border-blue-100',
  success: 'text-emerald-700 bg-emerald-50 border border-emerald-100',
  warning: 'text-amber-700 bg-amber-50 border border-amber-100',
  danger: 'text-red-700 bg-red-50 border border-red-100',
};

export default function Notifications() {
  const {
    notifications,
    markNotificationRead,
    addNotification,
    hasPermission,
    currentRole,
  } = useApp();

  const [showSendDialog, setShowSendDialog] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Local deletion state to mock deletion in global context
  const [deletedIds, setDeletedIds] = useState([]);

  // Send Notification form states
  const [sendTargetRole, setSendTargetRole] = useState('all');
  const [sendType, setSendType] = useState('info');
  const [sendTitle, setSendTitle] = useState('');
  const [sendMessageText, setSendMessageText] = useState('');

  const filteredNotifications = notifications.filter(n => {
    // 1. Locally deleted filter
    if (deletedIds.includes(n.id)) return false;

    // 2. Role-based filtering:
    // Non-admins can only see notifications matching their currentRole, or without a targetRole (global)
    if (currentRole !== ROLES.ADMIN) {
      if (n.targetRole && n.targetRole !== currentRole) return false;
    }

    // 3. Toolbar filters
    if (filterType !== 'all' && n.type !== filterType) return false;
    if (searchQuery && !n.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    
    return true;
  });

  const unreadCount = filteredNotifications.filter(n => !n.read).length;

  const toggleSelect = (id) => {
    setSelectedNotifications(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const markAllRead = () => {
    filteredNotifications.forEach(n => {
      if (!n.read) {
        markNotificationRead(n.id);
      }
    });
  };

  const deleteSelected = () => {
    setDeletedIds(prev => [...prev, ...selectedNotifications]);
    setSelectedNotifications([]);
  };

  const handleSendNotification = () => {
    if (!sendTitle || !sendMessageText) return;
    addNotification({
      title: sendTitle,
      message: sendMessageText,
      type: sendType,
      targetRole: sendTargetRole === 'all' ? null : sendTargetRole
    });
    setSendTitle('');
    setSendMessageText('');
    setSendTargetRole('all');
    setSendType('info');
    setShowSendDialog(false);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Notifications</h1>
          <p className="text-sm text-slate-500 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
          </p>
        </div>
        {hasPermission('notifications', 'send') && (
          <Button
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            onClick={() => setShowSendDialog(true)}
            data-testid="send-notification-btn"
          >
            <Send className="w-4 h-4" />
            Send Notification
          </Button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              placeholder="Search notifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 pl-10 bg-white border-slate-200"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-36 bg-white border-slate-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="danger">Danger</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          {selectedNotifications.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={deleteSelected}
              className="border-red-200 text-red-600 hover:bg-red-50 gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete ({selectedNotifications.length})
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={markAllRead}
            className="bg-white border-slate-200 gap-2"
          >
            <Check className="w-4 h-4" />
            Mark All Read
          </Button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="crm-card">
        <ScrollArea className="h-[calc(100vh-300px)]">
          {filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500">
              <Bell className="w-12 h-12 mb-4" />
              <p className="text-sm">No notifications found</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredNotifications.map((notification) => {
                const Icon = notificationIcons[notification.type] || Info;
                const colorClass = notificationColors[notification.type] || notificationColors.info;
                
                return (
                  <div
                    key={notification.id}
                    className={`flex items-start gap-4 p-4 hover:bg-slate-50 transition-colors ${
                      !notification.read ? 'bg-blue-50/70' : ''
                    }`}
                    data-testid={`notification-${notification.id}`}
                    onClick={() => {
                      if (!notification.read) {
                        markNotificationRead(notification.id);
                      }
                    }}
                  >
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedNotifications.includes(notification.id)}
                        onCheckedChange={() => toggleSelect(notification.id)}
                      />
                    </div>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClass}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className={`text-sm font-medium ${!notification.read ? 'text-slate-900' : 'text-slate-500'}`}>
                              {notification.title}
                            </p>
                            {!notification.read && (
                              <div className="w-2 h-2 rounded-full bg-blue-600" />
                            )}
                          </div>
                          <p className="text-sm text-slate-500 mt-1">{notification.message}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-xs text-slate-400">
                              {format(new Date(notification.timestamp), 'MMM dd, hh:mm a')}
                            </span>
                            {notification.targetRole && (
                              <Badge variant="outline" className="text-xs bg-slate-50 border-slate-200 text-slate-600 capitalize">
                                {notification.targetRole.replace('_', ' ')}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Send Notification Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Send Notification</DialogTitle>
            <DialogDescription className="text-slate-500">
              Send a notification to specific roles or users
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Target Audience</Label>
              <Select value={sendTargetRole} onValueChange={setSendTargetRole}>
                <SelectTrigger className="bg-slate-50/50 border-slate-200">
                  <SelectValue placeholder="Select target" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">All Users</SelectItem>
                  {Object.entries(ROLE_LABELS_MAP).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notification Type</Label>
              <Select value={sendType} onValueChange={setSendType}>
                <SelectTrigger className="bg-slate-50/50 border-slate-200">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="danger">Danger</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                className="bg-slate-50/50 border-slate-200"
                placeholder="Notification title"
                value={sendTitle}
                onChange={(e) => setSendTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                className="bg-slate-50/50 border-slate-200"
                placeholder="Notification message..."
                rows={4}
                value={sendMessageText}
                onChange={(e) => setSendMessageText(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSendDialog(false)}
              className="border-slate-200 text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              onClick={handleSendNotification}
            >
              <Send className="w-4 h-4" />
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
